import { trackSwitchInitiatePurchase } from '../../../helpers/switch/analytics';
import { channels } from '../../../constants';
import { ERROR_DEBUG_MESSAGES, PAYMENT_ERROR_GROUPS, PAYMENT_ERROR_TYPES } from '../constants/paymentErrors';
import startPurchase from '../helpers/startPurchase';
import { PAYMENT_STAGES } from '../PaymentContextProvider';

/*
  Responsible for moving from the payment method capture stage to:
  - Fulfillment (Authorization has been handled elsewhere, such as with Apple Pay)
  - 3DS (Payment Authorization)
*/
const paymentMethodStoredTransition =
  (paymentContext, { newMethodToken, isAddingPaymentMethod, paymentType }, runBeforeTransition) =>
  async dispatch => {
    const {
      resolvedUpgradeOffer,
      selectedPlanOverride,
      addPaymentToken,
      goToPaymentStage,
      raisePaymentError,
      restartPaymentFlow,
      isWebview,
    } = paymentContext;
    const channel = isWebview ? channels.app : channels.online;

    const purchasePayload = {
      targetOfferId: resolvedUpgradeOffer.id,
      targetOfferPlanCode: selectedPlanOverride?.planCode ?? resolvedUpgradeOffer.planCode,
      isFlexFlow: paymentContext.isFlexFlow,
      channel,
      ...(paymentContext.tokens?.flexUserMigrationFailed ? { flexUserMigrationFailed: true } : {}),
    };

    if (isAddingPaymentMethod) {
      addPaymentToken({ paymentMethod: newMethodToken, isUsingExistingMethod: false, paymentType });
      purchasePayload.paymentMethodToken = newMethodToken;
      purchasePayload.isAddingPaymentMethod = true;
    } else {
      addPaymentToken({ paymentMethod: newMethodToken ?? null, isUsingExistingMethod: true, paymentType });
      purchasePayload.isAddingPaymentMethod = false;
      if (newMethodToken) {
        purchasePayload.paymentMethodToken = newMethodToken;
      }
    }

    const {
      complete,
      threeDSecureActionTokenId,
      is3dsInitiatedOnEnforcedVerification,
      error,
      requiresFulfillment = true,
      flexUserMigrationFailed = false,
    } = await dispatch(startPurchase(purchasePayload));

    await runBeforeTransition?.();

    if (complete) {
      trackSwitchInitiatePurchase(true);
      if (flexUserMigrationFailed) {
        addPaymentToken({ flexUserMigrationFailed: true });
      }
      // All users (flex and non-flex) go to fulfillment
      // Flex-specific API handling is done in PaymentProcessing component
      goToPaymentStage(PAYMENT_STAGES.REQUIRES_FULFILLMENT);
    } else if (threeDSecureActionTokenId) {
      // Payment method requires authorization as we have received a 3DS identifier in the response
      trackSwitchInitiatePurchase(true);
      addPaymentToken({
        authorizationInput: threeDSecureActionTokenId,
        is3dsInitiatedOnEnforcedVerification,
        ...(flexUserMigrationFailed ? { flexUserMigrationFailed: true } : {}),
      });
      goToPaymentStage(PAYMENT_STAGES.REQUIRES_AUTHORIZATION);
    } else if (!requiresFulfillment && error === ERROR_DEBUG_MESSAGES.PAYMENT.NON_3DS_ERROR) {
      // Failure, prompt a user to try again
      trackSwitchInitiatePurchase(false);
      raisePaymentError({
        message: error,
        group: PAYMENT_ERROR_GROUPS.TOKENIZATION,
        type: PAYMENT_ERROR_TYPES.TOKENIZATION.ACTIVATION_FAILED_REFUND_MESSAGE,
      });
      restartPaymentFlow();
    } else {
      // Failure, prompt a user to try a different card
      trackSwitchInitiatePurchase(false);
      raisePaymentError({
        message: error,
        group: PAYMENT_ERROR_GROUPS.TOKENIZATION,
        type: PAYMENT_ERROR_TYPES.TOKENIZATION.INITIATING_PURCHASE,
      });
      restartPaymentFlow();
    }
  };

export default paymentMethodStoredTransition;
